import React, { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import Modal from 'react-modal';

import Loading from '../Loading';

import { AUTH_USER_QUERY } from '../../constants/gql';
import * as ROLES from '../../constants/roles';
import * as ROUTES from '../../constants/routes';

const modalStyles = {
  content: {
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    border: 0,
    background: "transparent",
  },
};

export const ME_CLINIC_USER = gql`
  query MeClinicUser {
    meClinicUser {
      id
      clinicCode
    }
  }
`;

export const USERS_BY_CLINIC = gql`
  query UsersByClinic(
    $clinicCode: String!
  ) {
    usersByClinic(
      clinicCode: $clinicCode
    ) {
      id
      firstName
      lastName
      phone
      email
      roles
      lastActivity
    }
  }
`;

export const UPDATE_CLINIC_USER_PERMISSIONS = gql`
  mutation UpdateClinicUserPermissions(
    $id: String!,
    $roles: [String]!
  ) {
    updateClinicUser(
      id: $id,
      data: {
        roles: $roles
      }
    ) {
      id
      firstName
      lastName
      phone
      email
      roles
      lastActivity
    }
  }
`;

export const DELETE_CLINIC_USER = gql`
  mutation DeleteClinicUser($id: String!) {
    deleteClinicUser(id: $id)
  }
`;

const ClinicUsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const history = useHistory();

  // Get auth user
  const { data: authUserData } = useQuery(AUTH_USER_QUERY);
  const { authUser } = authUserData;

  // Require auth and admin
  if (!authUser || !authUser.roles.includes(ROLES.CLINIC_ADMIN)) {
    // Redirect
    // TODO: Set "Admin required" message
    history.push(ROUTES.ROOT);
  }

  // Get user's clinic code
  const { data: meClinicUser } = useQuery(ME_CLINIC_USER);
  const clinicCode = meClinicUser?.meClinicUser?.clinicCode;

  // Setup get all Clinic users query
  // const { data } = useQuery(
  useQuery(
    USERS_BY_CLINIC,
    {
      variables: {
        clinicCode: clinicCode
      },
      fetchPolicy: 'network-only',
      onCompleted({ usersByClinic }) {
        // Sort by last name
        const sorted = _.sortBy(usersByClinic, (user) => user.lastName);

        setUsers(sorted);
        setLoading(false);
      },
      onError(error) {
        // TODO: show error message
        console.log('ClinicUsersList Query Error: ', error);

        setLoading(false);
      },
    }
  );

  // Setup update Clinic user permissions mutation
  const [updateUserPermissions] = useMutation(
    UPDATE_CLINIC_USER_PERMISSIONS,
    {
      onCompleted({ updateClinicUser: user }) {
        // TODO: show message

        // Update the users array
        const updatedUsers = [...users];
        const index = updatedUsers.findIndex((u) => (u.id === user.id));
        updatedUsers.splice(index, 1, user);

        // Set state
        setUsers(updatedUsers);
        setUpdating(false);
      },
      onError(error) {
        // TODO: show error message
        console.log('ClinicUsersList updateUserPermissions() error: ', error);

        setUpdating(false);
      },
    },
  );

  // Setup delete Radeas user mutation
  const [deleteUser] = useMutation(
    DELETE_CLINIC_USER,
    {
      onCompleted({ deleteClinicUser }) {
        // TODO: show message

        // Update the users array
        const updatedUsers = users.filter((u) => (u.id !== deleteClinicUser));

        // Set state
        setUsers(updatedUsers);
        setUpdating(false);
      },
      onError(error) {
        // TODO: show error message
        console.log('ClinicUsersList handleDelete() error: ', error);

        setUpdating(false);
      },
    },
  );

  useEffect(() => {
    Modal.setAppElement('body');
  }, []);

  const handleCheckbox = (event, user) => {
    // Prevent double-submits
    if (!updating) {
      setUpdating(true);

      const { name } = event.target;
      const roles = [...user.roles];

      // Toggle the role
      if (user.roles.includes(name)) {
        _.remove(roles, ((role) => (role === name)));
      } else {
        roles.push(name);
      }

      // Run mutation
      updateUserPermissions({
        variables: {
          id: user.id,
          roles: roles,
        },
      });
    }
  };

  const handleDelete = (user) => {
    const { id } = user;

    if (showModal) {
      handleCloseModal();
    }

    // Prevent double-submits
    if (!updating) {
      setUpdating(true);

      // Run mutation
      deleteUser({ variables: { id } });
    }
  };

  const handleOpenModal = (user) => {
    setShowModal(true);
    setUserToDelete(user);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setUserToDelete(null);
  };

  return (
    <div>
      {/*
      <h4 className="title is-4">Clinic Users</h4>
      <br />
      */}

      {loading ? (
        <Loading />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="table is-fullwidth is-striped is-hoverable">
            <thead>
            <tr>
              <th>
                Last Name
              </th>

              <th>
                First Name
              </th>

              <th>
                Email
              </th>

              <th>
                Phone
              </th>

              <th>
                Last Activity
              </th>

              <th className="has-text-centered">
                Clinic Admin{" "}
                <span
                  className="has-tooltip-arrow"
                  data-tooltip="Can edit and administer Clinic users."
                >
                    <i className="far fa-question-circle is-size-7"></i>
                  </span>
              </th>

              <th className="has-text-centered">
                Clinic User{" "}
                <span
                  className="has-tooltip-arrow"
                  data-tooltip="Default Clinic user, no admin privileges."
                >
                    <i className="far fa-question-circle is-size-7"></i>
                  </span>
              </th>

              <th></th>
            </tr>
            </thead>

            <tfoot>

            </tfoot>

            <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.lastName}</td>

                <td>{user.firstName}</td>

                <td>{user.email}</td>

                <td>{user.phone}</td>

                <td>{new Date(parseInt(user.lastActivity)).toDateString()}</td>

                <td className="has-text-centered">
                  <input
                    type="checkbox"
                    name={ROLES.CLINIC_ADMIN}
                    onChange={(event) => handleCheckbox(event, user)}
                    checked={user.roles.includes(ROLES.CLINIC_ADMIN) ? true : false}
                    disabled={authUser.roles.includes(ROLES.CLINIC_ADMIN) ? false : true}
                  />
                </td>

                <td className="has-text-centered">
                  <input
                    type="checkbox"
                    checked
                    disabled
                  />
                </td>

                <td>
                  <button
                    className="button has-text-danger-dark"
                    onClick={() => handleOpenModal(user)}
                  >
                    Delete User
                  </button>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onRequestClose={handleCloseModal}
        style={modalStyles}
      >
        <div className="modal is-active">
          <div className="modal-background" />
          <div className="modal-card">
            <header className="modal-card-head">
              <p className="modal-card-title">Delete User {userToDelete && userToDelete.email}?</p>
              <button
                className="delete"
                aria-label="close"
                onClick={handleCloseModal}
              />
            </header>

            <section className="modal-card-body">
              <p className="has-text-weight-semibold is-size-5">This cannot be undone.</p>

              <button
                className="button is-info mt-5 is-pulled-left"
                onClick={handleCloseModal}
              >
                Cancel Delete
              </button>

              <button
                className="button is-danger mt-5 is-pulled-right"
                onClick={() => handleDelete(userToDelete)}
              >
                <span className="icon is-small">
                  <i className="fas fa-times"></i>
                </span>
                <span>Confirm Delete</span>
              </button>
            </section>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClinicUsersList;
